import { OAuth2Client } from '@byteowls/capacitor-oauth2';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { isNativeApp, getCurrentPlatform } from '../utils/platformUtils';

const CLIENT_ID = '400451062835-ksi007ob0a6o5dvvkmvtipovogv62b3j';
const SCOPE = 'https://www.googleapis.com/auth/drive.file';

// Always use web URL for redirect (Google doesn't accept custom schemes)
const getRedirectUrl = () => {
  // For both native and web, use the web redirect URI
  // Google Drive OAuth requires a proper HTTPS URL
  return `${document.location.protocol}//${document.location.host}/callback`;
};

interface AuthResult {
  access_token: string;
  expires_in: number;
  token_type: string;
}

export class MobileGoogleDriveAuth {
  private static instance: MobileGoogleDriveAuth;
  private cachedToken: string | null = null;
  private tokenExpiry: number | null = null;

  static getInstance(): MobileGoogleDriveAuth {
    if (!MobileGoogleDriveAuth.instance) {
      MobileGoogleDriveAuth.instance = new MobileGoogleDriveAuth();
    }
    return MobileGoogleDriveAuth.instance;
  }

  private getAuthHeaders(): HeadersInit | null {
    // Get the Auth0 bearer token from localStorage
    const bearerToken = localStorage.getItem('bearer_token');
    if (!bearerToken) {
      return null; // Return null instead of throwing - let caller handle it
    }

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${bearerToken}`
    };
  }

  private isAuth0Authenticated(): boolean {
    return !!localStorage.getItem('bearer_token');
  }

  async getAccessToken(interactive: boolean = false): Promise<string> {
    console.log('[GoogleDrive] getAccessToken called', {
      interactive,
      hasCachedToken: !!this.cachedToken,
      cacheValid: this.cachedToken && this.tokenExpiry ? Date.now() < this.tokenExpiry : false,
      platform: Capacitor.getPlatform(),
      isNative: Capacitor.isNativePlatform()
    });

    // Check if we have a valid cached token in memory
    if (this.cachedToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      console.log('[GoogleDrive] Returning cached Drive token from memory');
      return this.cachedToken;
    }
    
    // Try to load token from localStorage (survives app restarts)
    const storedToken = localStorage.getItem('google_drive_token');
    if (storedToken) {
      try {
        const { access_token, expires_at } = JSON.parse(storedToken);
        if (access_token && expires_at && Date.now() < expires_at) {
          console.log('[GoogleDrive] Returning cached Drive token from localStorage');
          this.cachedToken = access_token;
          this.tokenExpiry = expires_at;
          return access_token;
        } else {
          // Token expired, remove it
          console.log('[GoogleDrive] Stored token expired, removing');
          localStorage.removeItem('google_drive_token');
        }
      } catch (e) {
        console.warn('[GoogleDrive] Failed to parse stored token:', e);
        localStorage.removeItem('google_drive_token');
      }
    }

    // No valid token, need to authenticate
    // Check platform first - only use Byteowls for native apps
    if (isNativeApp()) {
      return this.authenticateWithByteowls(interactive);
    } else {
      return this.authenticateForWeb(interactive);
    }
  }

  private async authenticateForWeb(interactive: boolean): Promise<string> {
    try {
      // Check if user is authenticated with Auth0 first
      if (!this.isAuth0Authenticated()) {
        throw new Error('Please log in with Auth0 first before connecting Google Drive');
      }

      console.log('[GoogleDrive] Starting OAuth authentication for web...', {
        interactive,
        platform: getCurrentPlatform(),
        hasAuth0Bearer: this.isAuth0Authenticated()
      });

      // For web, get auth URL from backend
      const authHeaders = this.getAuthHeaders();
      if (!authHeaders) {
        throw new Error('Authentication headers not available');
      }

      const response = await fetch('/api/google-drive/auth-url', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          interactive,
          isMobile: false,
          platform: getCurrentPlatform()
        })
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentication required. Please log in first.');
        }
        throw new Error('Failed to get OAuth URL from backend');
      }

      const { auth_url } = await response.json();
      console.log('[GoogleDrive] Got auth URL from backend for web');

      // Clear any previous OAuth errors and tokens before starting new auth attempt
      localStorage.removeItem('google_drive_oauth_error');
      localStorage.removeItem('google_drive_oauth_error_subtype');
      localStorage.removeItem('google_drive_oauth_token');
      console.log('[GoogleDrive] Cleared previous OAuth errors and tokens before opening popup');

      // For web, open the backend-generated URL in a popup manually
      // This ensures we use the exact URL the backend generated with proper PKCE
      return new Promise((resolve, reject) => {
        const popup = window.open(
          auth_url,
          'Google OAuth',
          'width=500,height=600,left=100,top=100'
        );
        
        if (!popup) {
          reject(new Error('Popup blocked. Please allow popups for this site.'));
          return;
        }
        
        // Poll for the token in localStorage (set by Callback.tsx after exchange)
        const checkInterval = setInterval(() => {
          try {
            // Check for OAuth errors first (set by Callback.tsx when error occurs)
            const oauthError = localStorage.getItem('google_drive_oauth_error');
            if (oauthError) {
              clearInterval(checkInterval);
              localStorage.removeItem('google_drive_oauth_error');
              const errorSubtype = localStorage.getItem('google_drive_oauth_error_subtype');
              localStorage.removeItem('google_drive_oauth_error_subtype');
              
              // Close popup if still open
              try {
                if (popup && !popup.closed) {
                  popup.close();
                }
              } catch (popupError) {
                try {
                  popup?.close();
                } catch (e) {
                  console.error('[GoogleDrive] Failed to close popup after error:', e);
                }
              }
              
              // If we got interaction_required/access_denied and this was a non-interactive attempt,
              // automatically retry with interactive: true to force the consent screen
              if ((oauthError === 'interaction_required' || oauthError === 'access_denied') && !interactive) {
                console.log('[GoogleDrive] Got interaction_required/access_denied on non-interactive attempt, retrying with interactive: true');
                clearInterval(checkInterval);
                try {
                  if (popup && !popup.closed) {
                    popup.close();
                  }
                } catch (e) {
                  // Ignore close errors
                }
                // Retry with interactive: true to force consent screen
                return this.authenticateForWeb(true).then(resolve).catch(reject);
              }
              
              // Provide a helpful error message
              let errorMessage = 'Authentication failed';
              if (oauthError === 'interaction_required' && errorSubtype === 'access_denied') {
                errorMessage = 'Access was denied. Please grant the required permissions when prompted.';
              } else if (oauthError === 'interaction_required') {
                errorMessage = 'Please complete the authentication and grant the required permissions.';
              } else if (oauthError === 'access_denied') {
                errorMessage = 'Authentication was cancelled or access was denied.';
              }
              
              reject(new Error(errorMessage));
              return;
            }
            
            // First check if token was stored in localStorage by the callback page
            const tokenData = localStorage.getItem('google_drive_oauth_token');
            if (tokenData) {
              try {
                const parsed = JSON.parse(tokenData);
                clearInterval(checkInterval);
                localStorage.removeItem('google_drive_oauth_token');
                
                // Close popup if still open (wrap in try-catch for COOP policy)
                try {
                  if (popup && !popup.closed) {
                    popup.close();
                  }
                } catch (popupError) {
                  // COOP policy may block access to popup.closed, but we can still try to close
                  try {
                    popup?.close();
                  } catch (e) {
                    console.error('[GoogleDrive] Failed to close popup after token received:', e);
                    console.error('[GoogleDrive] Popup may already be closed or inaccessible due to COOP policy');
                  }
                }
                
                // Store the token
                this.cachedToken = parsed.access_token;
                this.tokenExpiry = Date.now() + (parsed.expires_in * 1000);
                
                console.log('[GoogleDrive] Token received from callback page');
                resolve(parsed.access_token);
                return;
              } catch (e) {
                clearInterval(checkInterval);
                reject(new Error('Failed to parse token data'));
                return;
              }
            }
            
            // Check if popup was closed without completing auth
            // Wrap in try-catch to handle Cross-Origin-Opener-Policy restrictions
            try {
              if (popup.closed) {
                clearInterval(checkInterval);
                reject(new Error('Popup closed before authentication completed'));
                return;
              }
            } catch (popupError) {
              // COOP policy blocks access to popup.closed when navigating to different origin
              // This is expected when the popup navigates to Google OAuth
              // Continue polling - we'll detect completion via localStorage
            }
          } catch (e) {
            // Continue polling on any other error
            console.warn('[GoogleDrive] Error in popup polling:', e);
          }
        }, 500);
        
        // Timeout after 5 minutes
        setTimeout(() => {
          clearInterval(checkInterval);
          // Try to close popup (wrap in try-catch for COOP policy)
          try {
            if (popup && !popup.closed) {
              popup.close();
            }
          } catch (popupError) {
            // COOP policy may block access, but we can still try to close
            try {
              popup?.close();
            } catch (e) {
              console.error('[GoogleDrive] Failed to close popup on timeout:', e);
              console.error('[GoogleDrive] Popup may already be closed or inaccessible due to COOP policy');
            }
          }
          reject(new Error('Authentication timeout'));
        }, 300000);
      });
    } catch (error: any) {
      console.error('[GoogleDrive] OAuth authentication error:', error);
      if (error.error) {
        throw new Error(error.error || 'OAuth authentication failed');
      }
      throw error;
    }
  }

  private async authenticateWithByteowls(interactive: boolean): Promise<string> {
    try {
      // Check if user is authenticated with Auth0 first
      if (!this.isAuth0Authenticated()) {
        throw new Error('Please log in with Auth0 first before connecting Google Drive');
      }

      console.log('[GoogleDrive] Starting OAuth authentication with Byteowls plugin...', {
        interactive,
        platform: Capacitor.getPlatform(),
        isNative: Capacitor.isNativePlatform(),
        hasAuth0Bearer: this.isAuth0Authenticated()
      });
      
      // Use backend redirect URIs that are registered in Google Cloud Console
      const redirectUrl = getRedirectUrl();

      console.log('[GoogleDrive] OAuth config', {
        appId: CLIENT_ID,
        redirectUrl,
        scope: SCOPE,
        nativeConfig: {
          needsAssociatedDomain: redirectUrl.startsWith('https://'),
          usesCustomScheme: redirectUrl.startsWith('healthscores://')
        }
      });

      // For iOS native apps, use the Byteowls OAuth2 plugin with ASWebAuthenticationSession
      // Let the plugin handle PKCE and URL generation
      if (isNativeApp()) {
        console.log('[GoogleDrive] Using Byteowls OAuth2 plugin for native app', {
          redirectUrl,
          scope: SCOPE
        });

        try {
          // Set flag so OAuthCallbackHandler can capture the code via appUrlOpen
          localStorage.setItem('oauth_in_progress', 'byteowls_google');
          localStorage.removeItem('google_drive_oauth_code'); // Clear any previous code
          localStorage.removeItem('google_drive_oauth_error'); // Clear any previous error
          
          // Let the plugin open auth session (without PKCE)
          const result = await OAuth2Client.authenticate({
            appId: CLIENT_ID,
            authorizationBaseUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
            accessTokenEndpoint: '', // We'll exchange via our backend
            scope: SCOPE,
            responseType: 'code',
            redirectUrl: redirectUrl,
            logsEnabled: true,
            pkceEnabled: false, // No PKCE - backend isn't handling it for Byteowls flow
            additionalParameters: {
              access_type: 'offline',
              prompt: interactive ? 'consent' : 'select_account'
            },
            ios: {
              appId: CLIENT_ID,
              responseType: 'code',
              redirectUrl: redirectUrl
            }
          });

          console.log('[GoogleDrive] OAuth2Client.authenticate result:', result);

          // The plugin returns the access_token directly when pkceEnabled is true
          if (result.access_token) {
            console.log('[GoogleDrive] Got access token directly from plugin');
            this.cachedToken = result.access_token;
            const expiresIn = result.expires_in || 3600;
            this.tokenExpiry = Date.now() + (expiresIn * 1000);
            
            // Persist to localStorage
            localStorage.setItem('google_drive_token', JSON.stringify({
              access_token: result.access_token,
              expires_at: this.tokenExpiry
            }));
            
            // Also store in backend
            this.storeTokenInBackend(result.access_token, expiresIn).catch(console.warn);
            
            // Clear flag on success
            localStorage.removeItem('oauth_in_progress');
            
            return result.access_token;
          }
          
          // Fallback: if we only got code, exchange it
          const authorizationCode = result.authorization_code || result.code || (result as any).authorizationCode;
          if (authorizationCode) {
            console.log('[GoogleDrive] Got authorization code, exchanging for token via backend...');
            
            // Get auth headers
            const authHeaders = this.getAuthHeaders();
            if (!authHeaders) {
              throw new Error('Authentication headers not available');
            }
            
            // Exchange via backend with plugin's PKCE verifier
            const codeVerifier = (result as any).code_verifier || '';
            const token = await this.exchangeCodeForTokenWithVerifier(authorizationCode, codeVerifier);
            
            // Clear flag on success
            localStorage.removeItem('oauth_in_progress');
            
            return token;
          }
          
          // Clear flag before throwing
          localStorage.removeItem('oauth_in_progress');
          throw new Error('No access token or authorization code received from Google');
          
        } catch (error: any) {
          console.error('[GoogleDrive] OAuth2Client.authenticate error:', error);
          
          const errorMessage = error.message || error.toString() || '';
          const errorString = String(errorMessage);
          
          // ERR_NO_AUTHORIZATION_CODE means the plugin didn't see the redirect
          // This often happens when Universal Links intercept the redirect
          // Wait a moment for OAuthCallbackHandler to capture it via appUrlOpen
          if (errorString.includes('ERR_NO_AUTHORIZATION_CODE') || errorString.includes('NO_AUTHORIZATION_CODE')) {
            console.log('[GoogleDrive] Plugin did not receive authorization code, waiting for OAuthCallbackHandler to capture it...');
            
            // Wait up to 3 seconds for the code to be captured
            for (let i = 0; i < 6; i++) {
              await new Promise(resolve => setTimeout(resolve, 500));
              
              const codeData = localStorage.getItem('google_drive_oauth_code');
              if (codeData) {
                console.log('[GoogleDrive] Code captured by OAuthCallbackHandler after plugin failure, using it');
                try {
                  const { code } = JSON.parse(codeData);
                  localStorage.removeItem('google_drive_oauth_code');
                  localStorage.removeItem('oauth_in_progress');
                  
                  // Exchange the code directly with Google (no PKCE)
                  const token = await this.exchangeCodeDirectly(code);
                  return token;
                } catch (exchangeError) {
                  console.error('[GoogleDrive] Failed to exchange captured code:', exchangeError);
                  localStorage.removeItem('oauth_in_progress');
                  throw exchangeError;
                }
              }
              
              // Also check for token (in case oauth-callback.html already processed it)
              const tokenData = localStorage.getItem('google_drive_oauth_token');
              if (tokenData) {
                console.log('[GoogleDrive] Token already available from OAuthCallbackHandler, using it');
                try {
                  const parsed = JSON.parse(tokenData);
                  localStorage.removeItem('google_drive_oauth_token');
                  localStorage.removeItem('oauth_in_progress');
                  
                  this.cachedToken = parsed.access_token;
                  this.tokenExpiry = Date.now() + (parsed.expires_in * 1000);
                  
                  localStorage.setItem('google_drive_token', JSON.stringify({
                    access_token: parsed.access_token,
                    expires_at: this.tokenExpiry
                  }));
                  
                  this.storeTokenInBackend(parsed.access_token, parsed.expires_in).catch(console.warn);
                  
                  return parsed.access_token;
                } catch (parseError) {
                  console.error('[GoogleDrive] Failed to parse token data:', parseError);
                }
              }
            }
            
            console.log('[GoogleDrive] No code captured after waiting, checking one more time...');
          }
          
          // Check if OAuthCallbackHandler captured the code via appUrlOpen
          const codeData = localStorage.getItem('google_drive_oauth_code');
          if (codeData) {
            console.log('[GoogleDrive] Byteowls plugin failed but code was captured via appUrlOpen, using it');
            try {
              const { code } = JSON.parse(codeData);
              localStorage.removeItem('google_drive_oauth_code');
              localStorage.removeItem('oauth_in_progress');
              
              // Exchange the code directly with Google (no PKCE)
              const token = await this.exchangeCodeDirectly(code);
              return token;
            } catch (exchangeError) {
              console.error('[GoogleDrive] Failed to exchange captured code:', exchangeError);
              localStorage.removeItem('oauth_in_progress');
              throw exchangeError;
            }
          }
          
          // Check for token (from oauth-callback.html processing)
          const tokenData = localStorage.getItem('google_drive_oauth_token');
          if (tokenData) {
            console.log('[GoogleDrive] Token available from OAuthCallbackHandler, using it');
            try {
              const parsed = JSON.parse(tokenData);
              localStorage.removeItem('google_drive_oauth_token');
              localStorage.removeItem('oauth_in_progress');
              
              this.cachedToken = parsed.access_token;
              this.tokenExpiry = Date.now() + (parsed.expires_in * 1000);
              
              localStorage.setItem('google_drive_token', JSON.stringify({
                access_token: parsed.access_token,
                expires_at: this.tokenExpiry
              }));
              
              this.storeTokenInBackend(parsed.access_token, parsed.expires_in).catch(console.warn);
              
              return parsed.access_token;
            } catch (parseError) {
              console.error('[GoogleDrive] Failed to parse token data:', parseError);
            }
          }
          
          // Check for OAuth error in localStorage (set by OAuthCallbackHandler)
          const oauthError = localStorage.getItem('google_drive_oauth_error');
          if (oauthError) {
            localStorage.removeItem('google_drive_oauth_error');
            localStorage.removeItem('oauth_in_progress');
            throw new Error(`OAuth error: ${oauthError}`);
          }
          
          // Clear flag on error
          localStorage.removeItem('oauth_in_progress');
          
          // Check for OAuthSwift errors (from Byteowls plugin)
          // OAuthSwift error -5 typically means access_denied or user cancellation
          if (errorString.includes('OAuthSwiftError') || errorString.includes('OAuthSwift')) {
            const errorMatch = errorString.match(/error\s*(-?\d+)/i);
            const errorCode = errorMatch ? errorMatch[1] : null;
            
            if (errorCode === '-5' || errorCode === '5') {
              console.log('[GoogleDrive] OAuthSwift error -5 detected (access denied or cancelled)');
              throw new Error('Authentication was cancelled or access was denied');
            } else {
              console.log('[GoogleDrive] OAuthSwift error detected:', errorCode);
              throw new Error(`OAuth authentication failed: ${errorCode ? `error ${errorCode}` : 'unknown error'}`);
            }
          }
          
          // Check if user cancelled (various cancellation messages)
          if (errorString.includes('cancelled') || 
              errorString.includes('canceled') || 
              errorString.includes('USER_CANCELLED') ||
              errorString.includes('user_cancelled') ||
              errorString.includes('access_denied')) {
            throw new Error('Authentication was cancelled');
          }
          
          // For ERR_NO_AUTHORIZATION_CODE, provide a more helpful error
          if (errorString.includes('ERR_NO_AUTHORIZATION_CODE') || errorString.includes('NO_AUTHORIZATION_CODE')) {
            throw new Error('Authorization code not received. The redirect may have been intercepted. Please try again.');
          }
          
          throw error;
        }
      } else {
        // This should never happen - authenticateWithByteowls should only be called for native apps
        // If we reach here, it means isNativeApp() returned false, which is unexpected
        throw new Error('authenticateWithByteowls should only be called for native apps. Use authenticateForWeb for web browsers.');
      }
    } catch (error: any) {
      console.error('[GoogleDrive] OAuth authentication error:', error);
      if (error.error) {
        throw new Error(error.error || 'OAuth authentication failed');
      }
      throw error;
    }
  }

  private async storeTokenInBackend(accessToken: string, expiresIn: number): Promise<void> {
    // Store token in backend so it's associated with the Auth0 user
    // This allows token sync across devices
    try {
      const response = await fetch('/api/google-drive/token', {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          access_token: accessToken,
          expires_in: expiresIn
        })
      });
      if (!response.ok) {
        console.warn('[GoogleDrive] Backend token storage failed:', response.status);
      }
    } catch (error) {
      console.warn('[GoogleDrive] Error storing token in backend:', error);
    }
  }

  private async exchangeCodeForToken(authorizationCode: string, state: string): Promise<string> {
    try {
      console.log('[GoogleDrive] Exchanging authorization code for token via backend...');
      
      const authHeaders = this.getAuthHeaders();
      if (!authHeaders) {
        throw new Error('Authentication headers not available');
      }
      
      const response = await fetch('/callback', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          code: authorizationCode,
          state: state
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GoogleDrive] Token exchange failed:', response.status, errorText);
        throw new Error(`Failed to exchange authorization code: ${response.status}`);
      }

      const result = await response.json();
      if (result.access_token) {
        this.cachedToken = result.access_token;
        this.tokenExpiry = Date.now() + (result.expires_in * 1000);
        
        // Persist token to localStorage for app restarts
        localStorage.setItem('google_drive_token', JSON.stringify({
          access_token: result.access_token,
          expires_at: this.tokenExpiry
        }));
        console.log('[GoogleDrive] Token stored in localStorage');
        
        // Also store in backend for cross-device sync
        this.storeTokenInBackend(result.access_token, result.expires_in).catch(console.warn);
        
        return result.access_token;
      } else {
        throw new Error('No access token in response');
      }
    } catch (error) {
      console.error('[GoogleDrive] Token exchange error:', error);
      throw error;
    }
  }

  private async exchangeCodeDirectly(authorizationCode: string): Promise<string> {
    try {
      console.log('[GoogleDrive] Exchanging code directly (no state verification)...');
      
      const authHeaders = this.getAuthHeaders();
      if (!authHeaders) {
        throw new Error('Authentication headers not available');
      }
      
      // Exchange via backend without state verification (for Byteowls flow)
      const response = await fetch('/api/google-drive/exchange-code', {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          code: authorizationCode,
          redirect_uri: getRedirectUrl()
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GoogleDrive] Token exchange failed:', response.status, errorText);
        throw new Error(`Failed to exchange authorization code: ${response.status}`);
      }

      const result = await response.json();
      if (result.access_token) {
        this.cachedToken = result.access_token;
        this.tokenExpiry = Date.now() + (result.expires_in * 1000);
        
        // Persist token to localStorage
        localStorage.setItem('google_drive_token', JSON.stringify({
          access_token: result.access_token,
          expires_at: this.tokenExpiry
        }));
        console.log('[GoogleDrive] Token stored in localStorage');
        
        // Also store in backend
        this.storeTokenInBackend(result.access_token, result.expires_in).catch(console.warn);
        
        return result.access_token;
      } else {
        throw new Error('No access token in response');
      }
    } catch (error) {
      console.error('[GoogleDrive] Token exchange error:', error);
      throw error;
    }
  }

  private async exchangeCodeForTokenWithVerifier(authorizationCode: string, codeVerifier: string): Promise<string> {
    try {
      console.log('[GoogleDrive] Exchanging authorization code with PKCE verifier directly to Google...');
      
      // Exchange directly with Google using PKCE
      const redirectUrl = getRedirectUrl();
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: new URLSearchParams({
          code: authorizationCode,
          client_id: CLIENT_ID,
          redirect_uri: redirectUrl,
          grant_type: 'authorization_code',
          code_verifier: codeVerifier
        }).toString()
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[GoogleDrive] Token exchange failed:', response.status, errorText);
        throw new Error(`Failed to exchange authorization code: ${response.status}`);
      }

      const result = await response.json();
      if (result.access_token) {
        this.cachedToken = result.access_token;
        this.tokenExpiry = Date.now() + (result.expires_in * 1000);
        
        // Persist token to localStorage for app restarts
        localStorage.setItem('google_drive_token', JSON.stringify({
          access_token: result.access_token,
          expires_at: this.tokenExpiry
        }));
        console.log('[GoogleDrive] Token stored in localStorage');
        
        // Also store in backend for cross-device sync
        this.storeTokenInBackend(result.access_token, result.expires_in).catch(console.warn);
        
        return result.access_token;
      } else {
        throw new Error('No access token in response');
      }
    } catch (error) {
      console.error('[GoogleDrive] Token exchange error:', error);
      throw error;
    }
  }

  async clearAuth(): Promise<void> {
    this.cachedToken = null;
    this.tokenExpiry = null;
    
    // Clear from localStorage
    localStorage.removeItem('google_drive_token');
    localStorage.removeItem('google_drive_oauth_token'); // Legacy key
    
    // Clear from backend
    try {
      await fetch('/api/google-drive/revoke', {
        method: 'POST',
        headers: this.getAuthHeaders()
      });
    } catch (error) {
      console.error('Error revoking token:', error);
    }
    
    // Logout from OAuth2 plugin
    try {
      const redirectUrl = getRedirectUrl();
      await OAuth2Client.logout({
        appId: CLIENT_ID,
        authorizationBaseUrl: 'https://accounts.google.com/o/oauth2/v2/auth',
        redirectUrl: redirectUrl,
        scope: SCOPE
      });
    } catch (error) {
      console.error('Error logging out from OAuth2:', error);
    }
  }

  isAuthenticated(): boolean {
    return !!(this.cachedToken && this.tokenExpiry && Date.now() < this.tokenExpiry);
  }
}
